// notify.js
import React from 'react';
import ReactDOM from 'react-dom';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

// Create a portal container with high z-index
const notificationContainer = document.createElement('div');
Object.assign(notificationContainer.style, {
  position: 'fixed',
  zIndex: '9999999999',
  top: '24px',
  right: '24px',
  left: '24px',
  pointerEvents: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px'
});
document.body.appendChild(notificationContainer);

let notifications = [];
let listeners = [];
let nextId = 1;

export const notifyManager = {
  notify: (msg, code) => {
    const id = nextId++;
    let type, icon, colors;
    
    switch (code) {
      case 0: 
        type = 'error';
        icon = XCircle;
        colors = {
          bg: 'from-red-500/90 to-red-600/90',
          border: 'border-red-400/50',
          text: 'text-white',
          icon: 'text-red-100'
        };
        break;
      case 1: 
        type = 'success';
        icon = CheckCircle;
        colors = {
          bg: 'from-emerald-500/90 to-emerald-600/90',
          border: 'border-emerald-400/50',
          text: 'text-white',
          icon: 'text-emerald-100'
        };
        break;
      case 2:
        type = 'warning';
        icon = AlertTriangle;
        colors = {
          bg: 'from-amber-500/90 to-yellow-500/90',
          border: 'border-amber-400/50',
          text: 'text-black',
          icon: 'text-amber-900'
        };
        break;
      default: 
        type = 'info';
        icon = Info;
        colors = {
          bg: 'from-blue-500/90 to-blue-600/90',
          border: 'border-blue-400/50',
          text: 'text-white',
          icon: 'text-blue-100'
        };
    }
    
    const notification = {
      id,
      message: msg,
      type,
      icon,
      colors,
      timestamp: Date.now()
    };
    
    notifications.push(notification);
    listeners.forEach(listener => listener());
    
    // Auto-remove after 6 seconds
    setTimeout(() => {
      notifications = notifications.filter(n => n.id !== id);
      listeners.forEach(listener => listener());
    }, 6000);
  },
  
  remove: (id) => {
    notifications = notifications.filter(n => n.id !== id);
    listeners.forEach(listener => listener());
  },
  
  subscribe: (listener) => {
    listeners.push(listener);
    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  },
  
  getNotifications: () => notifications
};

// Individual Notification Component
const NotificationItem = ({ notification, onRemove }) => {
  const { id, message, icon: IconComponent, colors } = notification;
  const [isVisible, setIsVisible] = React.useState(false);
  const [isRemoving, setIsRemoving] = React.useState(false);

  React.useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleRemove = () => {
    setIsRemoving(true);
    setTimeout(() => onRemove(id), 300);
  };

  return (
    <div 
      className={`
        transform transition-all duration-300 ease-out pointer-events-auto
        ${isVisible && !isRemoving 
          ? 'translate-y-0 opacity-100 scale-100' 
          : 'translate-y-[-20px] opacity-0 scale-95'
        }
      `}
    >
      <div className={`
        relative max-w-md w-full mx-auto
        bg-gradient-to-r ${colors.bg}
        backdrop-blur-xl border ${colors.border}
        rounded-2xl shadow-2xl
        p-4 pr-12
        flex items-start space-x-3
        group hover:scale-105 transition-transform duration-200
      `}>
        {/* Animated background shimmer */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 rounded-2xl"></div>
        
        {/* Icon */}
        <div className={`flex-shrink-0 relative z-10`}>
          <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
            <IconComponent className={`${colors.icon} w-5 h-5`} />
          </div>
        </div>
        
        {/* Message */}
        <div className={`flex-1 relative z-10 ${colors.text} font-medium leading-relaxed`}>
          {message}
        </div>
        
        {/* Close Button */}
        <button
          onClick={handleRemove}
          className={`
            absolute top-3 right-3 relative z-10
            p-1.5 rounded-lg
            ${colors.text} opacity-70 hover:opacity-100
            hover:bg-white/20 transition-all duration-200
            transform hover:scale-110 active:scale-95
          `}
        >
          <X className="w-4 h-4" />
        </button>
        
        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20 rounded-b-2xl overflow-hidden">
          <div 
            className="h-full bg-white/30 rounded-b-2xl origin-left animate-progress-bar"
            style={{
              animation: 'progressBar 6s linear forwards'
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

// Main Notification Container
const NotificationRoot = () => {
  const [notifications, setNotifications] = React.useState([]);

  React.useEffect(() => {
    const updateNotifications = () => {
      setNotifications([...notifyManager.getNotifications()]);
    };

    return notifyManager.subscribe(updateNotifications);
  }, []);

  const handleRemove = (id) => {
    notifyManager.remove(id);
  };

  return (
    <>
      {/* Notifications */}
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onRemove={handleRemove}
        />
      ))}
      
      {/* CSS Animation Styles */}
      <style jsx global>{`
        @keyframes progressBar {
          from {
            transform: scaleX(1);
          }
          to {
            transform: scaleX(0);
          }
        }
        
        .animate-progress-bar {
          animation: progressBar 6s linear forwards;
        }
        
        /* Custom scrollbar for long messages */
        .notification-content::-webkit-scrollbar {
          width: 4px;
        }
        
        .notification-content::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
        }
        
        .notification-content::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 2px;
        }
        
        .notification-content::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
        
        /* Mobile responsiveness */
        @media (max-width: 640px) {
          .notification-item {
            margin: 0 16px;
            max-width: calc(100vw - 32px);
          }
        }
        
        /* Dark mode optimizations */
        @media (prefers-color-scheme: dark) {
          .notification-backdrop {
            backdrop-filter: blur(20px) saturate(180%);
          }
        }
        
        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .notification-item {
            border-width: 2px;
          }
        }
        
        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .notification-item,
          .notification-item * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </>
  );
};

// Render the notification component once at app start
ReactDOM.render(<NotificationRoot />, notificationContainer);

// Export the simple notify function
export const notify = (msg, code) => {
  notifyManager.notify(msg, code);
};

// Export additional utility functions
export const notifySuccess = (msg) => notify(msg, 1);
export const notifyError = (msg) => notify(msg, 0);
export const notifyWarning = (msg) => notify(msg, 2);
export const notifyInfo = (msg) => notify(msg, 3);

// Clear all notifications
export const clearAllNotifications = () => {
  notifyManager.getNotifications().forEach(n => notifyManager.remove(n.id));
};

// Initialize immediately
export default notify;